import type { GQL_ID, CommonSort } from '@@partner:types:graphql'
import type { ProductReviewInfo } from '@@partner:types:app'
import type { ResponsiveTableColumn } from '@components/common/ResponsiveTable/ResponsiveTable'

import React from 'react'
import { Button, Rate } from 'rsuite'
import { Link } from 'react-router-dom'
import ResponsiveTable from '@components/common/ResponsiveTable/ResponsiveTable'
import ProductReviewStatusLabel from '@components/common/ProductReviewStatusLabel'

import { SORT_ORDER_DESC } from '@@partner:config/graphql'

import './ProductReviewsTable.scss'

export interface ProductReviewsTableProps {
  tableSort: CommonSort
  loading: boolean
  productReviews: ProductReviewInfo[]
  generateEditUrl?: (productReviewId: GQL_ID) => string
  onSortColumn: (dataKey: string, sortType?: 'asc' | 'desc') => void
}

export default function ProductReviewsTable ({
  tableSort,
  loading = false,
  productReviews = [],
  generateEditUrl,
  onSortColumn
}: ProductReviewsTableProps) {
  const columns: ResponsiveTableColumn<ProductReviewInfo>[] = [
    {
      dataKey: 'id',
      header: 'ИД',
      sortable: true,
      flexGrow: 1,
      isCardBadge: true
    },
    {
      dataKey: 'product_id',
      header: 'ИД товара',
      sortable: true,
      flexGrow: 1
    },
    {
      dataKey: 'product',
      header: 'Товар',
      flexGrow: 2,
      isCardTitle: true,
      render: (review: ProductReviewInfo) => review.product?.name
    },
    {
      dataKey: 'customer',
      header: 'Покупатель',
      flexGrow: 2,
      render: (review: ProductReviewInfo) =>
        review.customer
          ? `${review.customer.first_name} ${review.customer.last_name}`
          : ''
    },
    {
      dataKey: 'status',
      header: 'Статус',
      sortable: true,
      flexGrow: 2,
      isCardBadge: true,
      render: (review: ProductReviewInfo) => (
        <ProductReviewStatusLabel status={review.status} />
      )
    },
    {
      dataKey: 'rating',
      header: 'Рейтинг',
      sortable: true,
      flexGrow: 2,
      render: (review: ProductReviewInfo) => (
        <Rate
          value={review.rating}
          readOnly
          className="ProductReviewsTable__Rate"
          size="xs"
        />
      )
    }
  ]

  const renderActions = generateEditUrl
    ? (review: ProductReviewInfo) => (
      <Button
        color="cyan"
        as={Link}
        to={generateEditUrl(review.id as GQL_ID)}
        size="xs"
      >
        Изменить
      </Button>
    )
    : undefined

  return (
    <ResponsiveTable<ProductReviewInfo>
      data={productReviews}
      columns={columns}
      loading={loading}
      rowHeight={100}
      sortColumn={tableSort.fieldName}
      sortType={tableSort.direction === SORT_ORDER_DESC ? 'desc' : 'asc'}
      onSortColumn={onSortColumn}
      renderActions={renderActions}
      emptyMessage="Нет отзывов"
    />
  )
}
